<i18n>
{
  "en": {
    "evolutive": "Evolutive graph",
    "price": "Price",
    "moneyLabel": "Euros",
    "sales": "Sales",
    "salesLabel": "Units",
    "margin": "Margin",
    "loading": "Loading...",
    "dates": "Dates",
    "filterServices": "Service to filter"
  },
  "es": {
    "evolutive": "Gráfico evolutivo",
    "price": "Precio",
    "moneyLabel": "Euros",
    "sales": "Ventas",
    "salesLabel": "Unidades",
    "margin": "Margen",
    "loading": "Cargando...",
    "dates": "Fechas",
    "filterServices": "Servicio a filtrar"
  }
}
</i18n>
<template>
  <div class="wrapper">
    <div class="head pt-2 d-flex flex-row justify-center align-center">
      <date-picker ref="datepicker" class="head pt-2" v-model="selectedDateRange"
                 :locale="userLocale" is-range trim-weeks>
        <template v-slot="{ togglePopover }">
          <v-btn @click="togglePopover({ placement: 'auto-start' })" rounded elevation="0" outlined
                 style="min-width: 260px">
            <v-icon class="mr-3">mdi-calendar</v-icon>
            <span>{{ dateToDisplay }}</span>
          </v-btn>
        </template>
      </date-picker>
      <v-select v-model="selectedService" :items="servicesToFilter" item-text="name"
                item-value="guid" :label="this.$t('filterServices')" @change="onChangeService"
                prepend-icon="mdi-clock-time-two-outline" class="mt-4 ml-6"/>
    </div>
    <v-btn-toggle v-model="selectedEvolutives" tile mandatory color="primary" group class="buttons"
                  :multiple="true" max="2">
      <v-btn v-for="kpi in evolutiveKpis" :value="kpi" :key="kpi">{{ $t(kpi) }}</v-btn>
    </v-btn-toggle>
    <bar-chart v-on="$listeners" class="chart py-5 px-2" :chart-data="chartData"
               :options="chartOptions" :width="600" :height="200"/>
  </div>
</template>
<script>
import Component from 'vue-class-component';
import { Components, MapGetters } from '@core/helpers/decorators';
import DatePicker from 'v-calendar/lib/components/date-picker.umd';
import BarChart from '@ulibs/unfrontcharts/components/BarChart';
import { Prop } from 'vue-property-decorator';
import Vue from 'vue';

@Component
@Components({ BarChart, DatePicker })
@MapGetters(['userLocale'])
export default class ProductBar extends Vue {
  @Prop({ type: Object, required: true }) evolutives;

  @Prop({ type: Date, required: true }) startDate;

  @Prop({ type: Date, required: true }) endDate;

  @Prop({ type: Boolean, default: false }) isLoading;

  @Prop({ type: Array, default: () => [] }) services;

  selectedEvolutives = ['price'];

  axisPositions = ['left', 'right'];

  axisMapping = {
    price: 'money',
    margin: 'money',
    sales: 'sales'
  }

  selectedService = null;

  chartPropsByType(selectedEvolutives) {
    return {
      price: {
        type: 'line',
        fill: false,
        order: 0,
        pointRadius: 2,
        lineTension: 0,
        borderColor: this.$vuetify.theme.currentTheme.primary,
        backgroundColor: this.$vuetify.theme.currentTheme.primary,
      },
      margin: {
        type: 'line',
        fill: false,
        order: 1,
        pointRadius: 2,
        lineTension: 0,
        borderWidth: selectedEvolutives.includes('price') ? 2 : 1,
        // Main color when it is the only line graph, secondary when another line is present
        borderColor: selectedEvolutives.includes('price') ? this.$vuetify.theme.currentTheme.secondary : this.$vuetify.theme.currentTheme.primary,
        backgroundColor: selectedEvolutives.includes('price') ? this.$vuetify.theme.currentTheme.secondary : this.$vuetify.theme.currentTheme.primary,
      },
      sales: {
        type: 'bar',
        order: 10,
        backgroundColor: this.$vuetify.theme.currentTheme.secondary
      },
    };
  }

  get evolutiveKpis() {
    return Object.keys(this.evolutives).filter((key) => !!this.evolutives[key]);
  }

  get yAxes() {
    const res = [];
    const ids = [];
    for (let index = 0; index < this.selectedEvolutives.length; ++index) {
      const selection = this.selectedEvolutives[index];
      let id = null;
      if (['margin', 'price'].includes(selection)) {
        if (!(ids.includes('money'))) {
          id = 'money';
        }
      } else {
        id = selection;
      }
      if (id) {
        ids.push(id);
        res.push({
          id,
          gridLines: {
            drawOnChartArea: false,
            display: true,
          },
          ticks: {
            beginAtZero: true
          },
          position: this.axisPositions[index],
          scaleLabel: {
            display: true,
            labelString: this.$t(`${id}Label`),
          },
        });
      }
    }
    return res;
  }

  get chartOptions() {
    return {
      scales: {
        yAxes: this.yAxes,
        xAxes: [{
          gridLines: {
            drawOnChartArea: false,
            display: true,
          },
          ticks: {
            // display: false to remove dates
          }
        }],
      },
      legend: {
        display: true
      },
      tooltips: {
        enabled: true
      },
      animation: {
        duration: 1000
      },
    };
  }

  get selectedDateRange() {
    return {
      start: this.startDate,
      end: this.endDate
    };
  }

  set selectedDateRange(range) {
    this.$emit('date-change', range.start, range.end, this.selectedService);
  }

  get servicesToFilter() {
    return ['', ...this.services];
  }

  get title() {
    return this.$t('evolutive');
  }

  get currentDatasets() {
    return this.selectedEvolutives.filter((selection) => this.evolutives[selection]).map((selection, index, array) => ({
      yAxisID: this.axisMapping[selection],
      label: this.$t(selection),
      data: Object.values(this.evolutives[selection]),
      borderWidth: 1,
      ...this.chartPropsByType(array)[selection],
    }));
  }

  get dateToDisplay() {
    return (this.selectedDateRange) ? this.selectedDateFormat : this.$t('dates');
  }

  get selectedDateFormat() {
    if (this.selectedDateRange.start.getTime() === this.selectedDateRange.end.getTime()) {
      return `${this.selectedDateRange.start.toLocaleDateString()}`;
    }
    return `${this.selectedDateRange.start.toLocaleDateString()} - ${this.selectedDateRange.end.toLocaleDateString()}`;
  }

  get labels() {
    const resultSet = new Set(this.selectedEvolutives.filter((selection) => this.evolutives[selection]).flatMap((selection) => (
      this.dateToString(Object.keys(this.evolutives[selection]))
    )));
    return [...resultSet];
  }

  get chartData() {
    return {
      labels: this.labels,
      datasets: this.currentDatasets
    };
  }

  dateToString(dateObject) {
    return dateObject.map((el) => {
      const date = new Date(el);
      return `${date.getDate()}/${date.getMonth() + 1}`;
    });
  }

  onChangeService() {
    this.$emit('date-change', this.selectedDateRange.start, this.selectedDateRange.end, this.selectedService);
  }
}
</script>

<style scoped>
.wrapper {
  display: grid;
  justify-items: center;
  grid-gap: 10px;
  grid-template-areas:
    'head'
    'buttons'
    'chart';
}

.title {
  grid-area: title;
}

.head {
  grid-area: head;
}

.buttons {
  grid-area: buttons;
}

.chart {
  grid-area: chart;
}
</style>
